import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {X} from '../../icons';
import {isOk} from '../../../utils/apiResponse';
import {WebhookApi} from '../../../api/alertRulesApi';
import {HELP_WEBHOOK, LinkToArticle} from '../../help_center/LinkToArticle';
import {PANEL_BODY_CLASS} from './SystemRulePanel';
import {destinationName} from './WebhookStatusCard';
import {DESTINATIONS, inferDestination, looksLikeWebhookUrl, testOutcome, webhookFromResponse} from './webhookModel';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), '
    + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Cuántos pasos tiene cada destino en el paso 2 (`alerts_center.webhook_setup.steps.<destino>.<n>`).
const STEP_COUNT = 3;

/**
 * El panel lateral para conectar el webhook de la agencia (README v2 §4.3), en 3 pasos: adónde (Slack ·
 * Google Chat · Make · Zapier · Otro), cómo copiar la dirección en ese destino, y pegarla y probar que llegue.
 *
 * - Se abre al prender el webhook de una fila sin URL (`pendingRule`: «Guardar y mandar esta alerta» guarda
 *   la URL y después prende esa fila), desde «Conectar» y desde «Arreglar».
 * - «Probar» es el de siempre (`PUT webhook-url/test`) y dice lo que pasó de verdad (`testOutcome`).
 * - Guardar no exige que la prueba llegue: un destino puede rechazar la prueba y aceptar los avisos (está
 *   pendiente el formato por destino, D5). Si la prueba falló, se dice al lado del botón.
 * - `events`: los eventos que viajan (los «Cambios en tus cuentas» de la agencia y los de las filas con webhook
 *   prendido, `eventsForSave`); `null` si no se pudieron leer.
 * - El destino: el que la agencia ya dijo o el que se deduce de la dirección; si no se sabe, no se elige ninguno
 *   (antes arrancaba en Slack y se guardaba Slack, review C-av2-fe-centro-17).
 * - `onSaved({url, destination, webhook})` lleva el `webhook` que devolvió el PUT, si vino; `onTested()` avisa una
 *   prueba con éxito (despausa). Con una fila pendiente, el aviso de éxito lo da quien prende la fila.
 * - Modal: foco adentro (Tab da la vuelta), Escape y la X cierran, el foco vuelve a quien lo abrió. Por
 *   portal a `body`, que en la 2.1 lleva `ui-v2`; a 390 ocupa la pantalla entera.
 *
 * Los pasos por destino son copy propuesto: falta probar un envío real a Slack y a Google Chat (D5).
 */
export function WebhookSetupDrawer({pendingRuleTitle = null, initialUrl = '', initialDestination = null, events = null,
                                       onSaved, onTested = null, onClose}) {
    const {t} = useTranslation();
    const [destination, setDestination] = useState(() => (DESTINATIONS.includes(initialDestination)
        ? initialDestination : inferDestination(initialUrl)));
    // Mientras no se elija a mano, el destino sigue a la dirección pegada.
    const [picked, setPicked] = useState(DESTINATIONS.includes(initialDestination));
    const [url, setUrl] = useState(initialUrl || '');
    const [test, setTest] = useState(null);
    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const panelRef = useRef(null);
    const openerRef = useRef(typeof document !== 'undefined' ? document.activeElement : null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        const opener = openerRef.current;
        if (panelRef.current) panelRef.current.focus();
        document.body.classList.add(PANEL_BODY_CLASS);
        const onKey = (event) => {
            if (event.key === 'Escape') onCloseRef.current();
        };
        document.addEventListener('keydown', onKey);
        return () => {
            mounted.current = false;
            document.removeEventListener('keydown', onKey);
            document.body.classList.remove(PANEL_BODY_CLASS);
            if (opener && opener.focus) opener.focus();
        };
    }, []);

    const trapTab = (event) => {
        if (event.key !== 'Tab' || !panelRef.current) return;
        const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    const trimmed = url.trim();
    const urlOk = looksLikeWebhookUrl(trimmed);

    const changeUrl = (value) => {
        setUrl(value);
        setTest(null);
        setError(null);
        if (!picked) setDestination(inferDestination(value));
    };

    const pickDestination = (item) => {
        setDestination(item);
        setPicked(true);
    };

    const runTest = () => {
        if (!urlOk) {
            setError('alerts_center.webhook_setup.error_url');
            return;
        }
        if (testing) return;
        setTesting(true);
        setTest(null);
        WebhookApi.test({webhookUrl: trimmed, events: events || []}).then((response) => {
            if (!mounted.current) return;
            setTesting(false);
            const outcome = testOutcome(response);
            setTest(outcome);
            if (outcome.tone === 'ok' && onTested) onTested();
        });
    };

    const save = () => {
        if (!urlOk) {
            setError('alerts_center.webhook_setup.error_url');
            return;
        }
        if (saving) return;
        setSaving(true);
        const chosen = destination || inferDestination(trimmed) || 'other';
        WebhookApi.save({webhookUrl: trimmed, events, destination: chosen}).then((response) => {
            if (!mounted.current) return;
            setSaving(false);
            if (!isOk(response)) {
                toast.error(t('alerts_center.webhook.save_error'));
                return;
            }
            // Con una fila pendiente, «esta alerta ya sale por ahí» lo dice quien la prende, cuando la prendió
            // (review C-av2-fe-centro-18).
            if (!pendingRuleTitle) toast.success(t('alerts_center.webhook.saved'));
            onSaved({url: trimmed, destination: chosen, webhook: webhookFromResponse(response)});
        });
    };

    const destName = destination ? destinationName(destination, t) : null;
    const testFailed = test && test.tone === 'error';
    // Una dirección que no sirve se dice debajo del campo apenas está escrita: una `http://` precargada dejaba
    // Guardar deshabilitado sin motivo (review C-av2-fe-centro-26).
    const shownError = error || (trimmed && !urlOk ? 'alerts_center.webhook_setup.error_url' : null);

    return createPortal(
        <div className="alc-drawer-layer">
            <div className="alc-drawer-backdrop" onMouseDown={onClose}/>
            <aside className="alc-drawer alc-webhook-drawer" role="dialog" aria-modal="true" aria-labelledby="alc-webhook-drawer-title"
                   ref={panelRef} tabIndex={-1} data-testid="alc-webhook-drawer" onKeyDown={trapTab}>
                <header className="alc-drawer__head">
                    <div>
                        <span className="alc-drawer__eyebrow">{t('alerts_center.webhook_setup.eyebrow')}</span>
                        <h2 id="alc-webhook-drawer-title" className="alc-drawer__title">{t('alerts_center.webhook_setup.title')}</h2>
                        {pendingRuleTitle && (
                            <p className="alc-drawer__subtitle" data-testid="alc-webhook-pending">
                                {t('alerts_center.webhook_setup.pending', {title: pendingRuleTitle, interpolation: {escapeValue: false}})}
                            </p>
                        )}
                    </div>
                    <button type="button" className="alc-drawer__close" aria-label={t('alerts_center.close')} onClick={onClose}>
                        <X size={18} weight="bold"/>
                    </button>
                </header>

                <div className="alc-drawer__body">
                    <section className="alc-drawer__section" aria-labelledby="alc-webhook-step-1">
                        <h3 id="alc-webhook-step-1" className="alc-drawer__section-title">{t('alerts_center.webhook_setup.step_1')}</h3>
                        <div className="alc-webhook-drawer__tiles">
                            {DESTINATIONS.map((item) => (
                                <button key={item} type="button" aria-pressed={item === destination}
                                        className={`alc-webhook-drawer__tile${item === destination ? ' -on' : ''}`}
                                        data-destination={item} onClick={() => pickDestination(item)}>
                                    <span className="alc-webhook-drawer__tile-name">{destinationName(item, t)}</span>
                                    <span className="alc-webhook-drawer__tile-hint">{t(`alerts_center.webhook_setup.dest_hint.${item}`)}</span>
                                </button>
                            ))}
                        </div>
                    </section>

                    <section className="alc-drawer__section" aria-labelledby="alc-webhook-step-2">
                        <h3 id="alc-webhook-step-2" className="alc-drawer__section-title">
                            {/* «Otro sistema» no es un producto: «Copia la dirección en Otro sistema» se leía así (QA-av2c-03). */}
                            {destName && destination !== 'other'
                                ? t('alerts_center.webhook_setup.step_2', {dest: destName, interpolation: {escapeValue: false}})
                                : t(destination === 'other' ? 'alerts_center.webhook_setup.step_2_other'
                                    : 'alerts_center.webhook_setup.step_2_generic')}
                        </h3>
                        {destination ? (
                            <ol className="alc-webhook-drawer__steps" data-testid="alc-webhook-steps">
                                {Array.from({length: STEP_COUNT}, (_, index) => (
                                    <li key={index}>{t(`alerts_center.webhook_setup.steps.${destination}.${index + 1}`)}</li>
                                ))}
                            </ol>
                        ) : <p className="alc-hint alc-webhook-drawer__pick">{t('alerts_center.webhook_setup.pick_destination')}</p>}
                        <LinkToArticle article={HELP_WEBHOOK} plain/>
                    </section>

                    <section className="alc-drawer__section" aria-labelledby="alc-webhook-step-3">
                        <h3 id="alc-webhook-step-3" className="alc-drawer__section-title">
                            <label htmlFor="alc-webhook-drawer-url">{t('alerts_center.webhook_setup.step_3')}</label>
                        </h3>
                        <div className="alc-webhook__url">
                            <input id="alc-webhook-drawer-url" type="url" value={url} placeholder="https://" autoComplete="off"
                                   aria-invalid={shownError ? 'true' : 'false'}
                                   aria-describedby={shownError ? 'alc-webhook-drawer-error' : undefined}
                                   onChange={(event) => changeUrl(event.target.value)}/>
                            <button type="button" className="alc-btn -ghost" disabled={!trimmed || testing} onClick={runTest}>
                                {testing ? t('alerts_center.webhook.testing') : t('alerts_center.webhook.test')}
                            </button>
                        </div>
                        {shownError && (
                            <p id="alc-webhook-drawer-error" className="alc-field-error" role={error ? 'alert' : undefined}>{t(shownError)}</p>
                        )}
                        {test && (
                            <p className={`alc-webhook__test -${test.tone}`} role="status" data-testid="alc-webhook-drawer-test">
                                {t(test.key, {code: test.code || '—'})}
                                {test.tone === 'ok' && destName && (
                                    <>{' '}{t('alerts_center.webhook_setup.test_ok_check', {dest: destName, interpolation: {escapeValue: false}})}</>
                                )}
                            </p>
                        )}
                    </section>
                </div>

                <footer className="alc-drawer__foot">
                    {testFailed && <p className="alc-hint alc-drawer__foot-note">{t('alerts_center.webhook_setup.save_anyway')}</p>}
                    <div className="alc-drawer__actions">
                        <button type="button" className="alc-btn -ghost" onClick={onClose}>{t('alerts_center.webhook_setup.not_now')}</button>
                        <button type="button" className="alc-btn -primary" disabled={!urlOk || saving} onClick={save}
                                data-testid="alc-webhook-drawer-save">
                            {saving ? t('alerts_center.saving')
                                : t(pendingRuleTitle ? 'alerts_center.webhook_setup.save_and_send' : 'alerts_center.webhook_setup.save')}
                        </button>
                    </div>
                </footer>
            </aside>
        </div>,
        document.body,
    );
}

export default WebhookSetupDrawer;
